
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { getAllBlogPosts } from "@/utils/blogUtils";
import { BlogPost } from "@/types/blog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Plus, RefreshCcw, Search, Filter, LayoutGrid, LayoutList } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { BlogPostTable } from "@/components/blog/BlogPostTable";
import { DeletePostDialog } from "@/components/blog/DeletePostDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const BlogManager = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<BlogPost | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [filterStatus, setFilterStatus] = useState<"all" | "published" | "draft">("all");

  const fetchPosts = async () => {
    setLoading(true);
    try {
      console.log("Fetching all blog posts including drafts...");
      const blogPosts = await getAllBlogPosts(false); // Get all posts including drafts
      console.log(`Fetched ${blogPosts.length} blog posts`);
      
      if (blogPosts.length === 0) {
        // Additional debugging for empty results
        console.log("No posts found, checking database directly...");
        const { data, error } = await supabase
          .from('blog_posts')
          .select('id, title, slug, publish_date, author, is_published, content, post_summary')
          .order('publish_date', { ascending: false });
          
        if (error) {
          console.error("Database direct check error:", error);
        } else {
          console.log("Direct database check found:", data?.length, "posts");
          if (data && data.length > 0) {
            console.log("Sample data from DB:", data.slice(0, 3));
            
            // If we have data directly but not through getAllBlogPosts, 
            // let's transform the data manually to ensure it matches the BlogPost type
            const transformedPosts: BlogPost[] = data.map((post: any) => ({
              id: post.id,
              title: post.title || "Untitled",
              slug: post.slug || "",
              date: post.publish_date ? new Date(post.publish_date).toISOString() : new Date().toISOString(),
              author: post.author || "edgefi Team",
              tags: [], // Use empty tags array since we don't have tags in the direct query
              isPublished: post.is_published,
              excerpt: post.post_summary || (post.content ? post.content.replace(/<[^>]*>/g, '').substring(0, 150) + '...' : ""),
              content: post.content || "",
            }));
            
            console.log("Transformed posts:", transformedPosts.length);
            setPosts(transformedPosts);
            setLoading(false);
            return;
          }
        }
      } else {
        // Log some sample posts if available
        console.log("Sample posts:", blogPosts.slice(0, 3));
      }
      
      setPosts(blogPosts);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      toast({
        title: "Error fetching blog posts",
        description: "Could not load blog posts. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleCreateNew = () => {
    navigate("/admin/blog/new");
  };

  const handleEdit = (slug: string) => {
    navigate(`/admin/blog/${slug}`);
  };

  const handleDeleteClick = (post: BlogPost) => {
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!postToDelete) return;
    
    setDeleting(true);
    try {
      // Delete the post from the database
      const { error } = await supabase
        .from("blog_posts")
        .delete()
        .eq("slug", postToDelete.slug);
        
      if (error) throw error;
      
      // Update the UI
      setPosts(posts.filter(post => post.slug !== postToDelete.slug));
      
      toast({
        title: "Success",
        description: "Blog post deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting post:", error);
      toast({
        title: "Error",
        description: "Failed to delete blog post",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setPostToDelete(null);
    }
  };

  const filteredPosts = posts
    .filter(post => {
      if (filterStatus === "published") return post.isPublished;
      if (filterStatus === "draft") return !post.isPublished;
      return true; // "all"
    })
    .filter((post) =>
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
      post.author?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const getStatusCount = (status: "published" | "draft") => {
    return posts.filter(post => status === "published" ? post.isPublished : !post.isPublished).length;
  };

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Blog Management | edgefi Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Blog Management</h1>
          <p className="text-gray-500">Manage your blog posts, articles, and content</p>
        </div>
        <div className="flex gap-4">
          <Button 
            onClick={fetchPosts} 
            variant="outline" 
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCcw size={16} />
            Refresh
          </Button>
          <Button 
            onClick={handleCreateNew}
            className="flex items-center gap-2 bg-edgefi-purple hover:bg-edgefi-purple/90"
          >
            <Plus size={16} />
            Create New Post
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-0">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <CardTitle>All Blog Posts</CardTitle>
              <CardDescription>Manage, edit, or delete your blog posts</CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 pt-4">
            <Tabs
              value={filterStatus}
              onValueChange={(value) => setFilterStatus(value as "all" | "published" | "draft")}
              className="w-full sm:w-auto"
            >
              <TabsList className="grid grid-cols-3 w-full sm:w-auto">
                <TabsTrigger value="all">
                  All <Badge variant="secondary" className="ml-1 bg-gray-200">{posts.length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="published">
                  Published <Badge variant="secondary" className="ml-1 bg-gray-200">{getStatusCount("published")}</Badge>
                </TabsTrigger>
                <TabsTrigger value="draft">
                  Drafts <Badge variant="secondary" className="ml-1 bg-gray-200">{getStatusCount("draft")}</Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex gap-2 justify-between w-full sm:w-auto">
              <div className="relative flex-grow sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input 
                  placeholder="Search posts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon">
                      <Filter size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem>Most recent first</DropdownMenuItem>
                    <DropdownMenuItem>Oldest first</DropdownMenuItem>
                    <DropdownMenuItem>A-Z</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>Filter by tag</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button 
                  variant={viewMode === "list" ? "default" : "outline"}
                  size="icon"
                  onClick={() => setViewMode("list")}
                >
                  <LayoutList size={16} />
                </Button>
                <Button 
                  variant={viewMode === "grid" ? "default" : "outline"}
                  size="icon"
                  onClick={() => setViewMode("grid")}
                >
                  <LayoutGrid size={16} />
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-12 h-12 border-4 border-edgefi-purple/30 border-t-edgefi-purple rounded-full animate-spin"></div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              {searchTerm || filterStatus !== "all" ? (
                <div className="space-y-4">
                  <p className="text-lg text-gray-500">No blog posts found matching your criteria</p>
                  <div className="flex justify-center gap-2">
                    {searchTerm && (
                      <Button 
                        variant="outline" 
                        onClick={() => setSearchTerm("")}
                      >
                        Clear Search
                      </Button>
                    )}
                    {filterStatus !== "all" && (
                      <Button 
                        variant="outline" 
                        onClick={() => setFilterStatus("all")}
                      >
                        Show All Posts
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-lg text-gray-500">No blog posts found</p>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 mx-auto"
                    onClick={handleCreateNew}
                  >
                    <Plus size={16} />
                    Create Your First Post
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              <p className="mb-4 text-sm text-gray-500">
                Showing {filteredPosts.length} {filteredPosts.length === 1 ? 'blog post' : 'blog posts'}
                {filterStatus !== "all" && ` (${filterStatus})`}
                {searchTerm && ` matching "${searchTerm}"`}
              </p>

              {viewMode === "list" ? (
                <BlogPostTable
                  posts={filteredPosts}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredPosts.map(post => (
                    <Card key={post.id || post.slug} className="overflow-hidden">
                      {post.coverImage && (
                        <div className="aspect-video overflow-hidden">
                          <img 
                            src={post.coverImage} 
                            alt={post.title} 
                            className="w-full h-full object-cover transition-transform hover:scale-105"
                          />
                        </div>
                      )}
                      <CardContent className="p-4">
                        <h3 className="font-medium text-lg line-clamp-2">{post.title}</h3>
                        <div className="flex items-center justify-between mt-2 mb-3">
                          <Badge variant={post.isPublished ? "default" : "outline"} className="text-xs">
                            {post.isPublished ? "Published" : "Draft"}
                          </Badge>
                          <span className="text-xs text-gray-500">{new Date(post.date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between mt-4">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(post.slug)}>
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteClick(post)}>
                            Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <DeletePostDialog
        post={postToDelete}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDelete}
        isDeleting={deleting}
      />
    </div>
  );
};

export default BlogManager;
